/**
 * /invite/:token — 公开的邀请接受页(teacher-role spec §5.1)。
 * 展示邮箱/学科 → 设密码(≥12)→ PIPL 数据使用声明必勾 → accept。
 * 错误态(过期/撤销/已用)给明确文案 + 联系园长指引(边界5)。
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

interface InviteInfo {
  email: string;
  name: string | null;
  role: string;
  subjects: { id: string; slug: string; name: string }[];
}

export default function InviteAcceptPage() {
  const { token } = useParams<{ token: string }>();
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    void (async () => {
      const resp = await fetch(`/api/school/public/invitations/${token}`);
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setFatal(body?.detail?.message ?? "邀请链接无效,请联系园长重新获取");
        return;
      }
      setInfo(body as InviteInfo);
    })();
  }, [token]);

  async function submit() {
    setErr(null);
    if (pw.length < 12) return setErr("密码至少 12 位");
    if (pw !== pw2) return setErr("两次输入的密码不一致");
    setBusy(true);
    try {
      const resp = await fetch(`/api/school/public/invitations/${token}/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password: pw, data_use_acknowledged: true }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setErr(body?.detail?.message ?? `激活失败(${resp.status}),请联系园长`);
        return;
      }
      setDone(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-lg">
        <h1 className="text-xl font-semibold text-slate-800 mb-1">加入学校 AI 平台</h1>

        {fatal && (
          <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            {fatal}
          </div>
        )}

        {done && (
          <div className="mt-4">
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 mb-4">
              ✓ 账户已激活。请用邮箱和刚设置的密码登录。
            </div>
            {/* 跳首页触发常规 OIDC 登录(ProtectedRoute→signIn),
                而非 /login 备用邮箱密码页 —— 后者仅在 OIDC 不可用时用。 */}
            <a
              href="/"
              className="block w-full rounded-lg bg-sky-600 py-2.5 text-center text-sm font-medium text-white hover:bg-sky-700"
            >
              去登录 →
            </a>
          </div>
        )}

        {info && !done && !fatal && (
          <>
            <p className="text-sm text-slate-500 mb-4">
              {info.email}
              {info.subjects.length > 0 && (
                <span className="block mt-1">
                  学科:
                  {info.subjects.map((s) => (
                    <span key={s.id} className="ml-1 px-1.5 py-0.5 rounded bg-sky-50 text-sky-700 text-xs">
                      {s.name}
                    </span>
                  ))}
                </span>
              )}
            </p>
            <label className="block text-sm mb-3">
              <span className="text-slate-600">设置密码(至少 12 位)</span>
              <input
                type="password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="block text-sm mb-4">
              <span className="text-slate-600">确认密码</span>
              <input
                type="password"
                value={pw2}
                onChange={(e) => setPw2(e.target.value)}
                className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </label>
            {err && <p className="mb-3 text-xs text-rose-600">{err}</p>}
            <button
              onClick={submit}
              disabled={busy}
              className="w-full rounded-lg bg-sky-600 py-2.5 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-50"
            >
              {busy ? "激活中…" : "激活账户"}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
